import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { useHouseholdMembership } from '../../contexts/HouseholdMembershipContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';

type Props = {
  /** Compact control for the top bar (no bottom margin). */
  compact?: boolean;
};

/** Mine / Household segmented control. */
export function HouseholdViewSwitcher({ compact = false }: Props) {
  const { view, setView, memberCount } = useHouseholdMembership();

  if (memberCount < 2) return null;

  return (
    <View
      style={[styles.wrap, compact && styles.wrapCompact]}
      accessibilityRole="tablist"
    >
      {(
        [
          { id: 'personal', label: 'Mine' },
          { id: 'combined', label: 'Household' },
        ] as const
      ).map((opt) => {
        const active = view === opt.id;
        return (
          <Pressable
            key={opt.id}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => setView(opt.id)}
            style={({ pressed }) => [
              styles.seg,
              compact && styles.segCompact,
              active && styles.segActive,
              pressed && styles.pressed,
            ]}
          >
            <Text style={[styles.segText, active && styles.segTextActive]}>{opt.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    padding: 3,
    marginBottom: spacing.md,
  },
  wrapCompact: {
    alignSelf: 'center',
    marginBottom: 0,
    marginRight: spacing.sm,
  },
  seg: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
  },
  segCompact: {
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 6,
  },
  segActive: {
    backgroundColor: 'rgba(61, 214, 164, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.4)',
  },
  segText: { ...typography.caption, fontWeight: '600', color: colors.textMuted },
  segTextActive: { color: colors.accent, fontWeight: '800' },
  pressed: { opacity: 0.9 },
});
